import { useState, useEffect, createContext, useContext } from 'react';
import { supabase } from '../lib/supabase';
import { fetchUserProfile, resolveAdminAccess } from '../utils/adminAccess';
import { devLog } from '../utils/devLog';

const AuthContext = createContext({});
const AUTH_INIT_TIMEOUT_MS = 8000;
const PROFILE_FETCH_TIMEOUT_MS = 5000;
const AUTH_LOG_PREFIX = '[AUTH]';
const WAS_EVER_AUTHENTICATED_KEY = 'colobongo_was_ever_authenticated';

/**
 * Check if a user was ever authenticated on this device.
 * Returns true even if the user has signed out.
 * This is used to skip onboarding and tutorial for returning users.
 */
export function hasUserEverBeenAuthenticatedOnDevice() {
  try {
    return localStorage.getItem(WAS_EVER_AUTHENTICATED_KEY) === 'true';
  } catch (_error) {
    return false;
  }
}

function withTimeout(promise, ms, label = 'timeout') {
  let timerId;
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      timerId = setTimeout(() => reject(new Error(label)), ms);
    }),
  ]).finally(() => {
    if (timerId) clearTimeout(timerId);
  });
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminRole, setAdminRole] = useState('guest');
  const [adminReason, setAdminReason] = useState('none');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let isInitialSettled = false;

    devLog.info(`${AUTH_LOG_PREFIX} init start`);

    const safeSetLoading = (value) => {
      if (cancelled) return;
      devLog.info(`${AUTH_LOG_PREFIX} setLoading`, { value });
      setLoading(value);
    };

    const hydrateAuthState = async (nextUser) => {
      if (cancelled) return;
      devLog.info(`${AUTH_LOG_PREFIX} hydrateAuthState start`, {
        userId: nextUser?.id ?? null,
      });
      setUser(nextUser ?? null);

      if (nextUser) {
        // Mark that a user has been authenticated on this device
        try {
          localStorage.setItem(WAS_EVER_AUTHENTICATED_KEY, 'true');
        } catch (_error) {
          // best effort persistence
        }
      }

      if (!nextUser) {
        setProfile(null);
        setIsAdmin(false);
        setAdminRole('guest');
        setAdminReason('none');
        devLog.info(`${AUTH_LOG_PREFIX} guest mode applied`);
        return;
      }

      let nextProfile = null;
      try {
        nextProfile = await withTimeout(
          fetchUserProfile(nextUser.id),
          PROFILE_FETCH_TIMEOUT_MS,
          'profile_fetch_timeout'
        );
      } catch (error) {
        devLog.warn(`${AUTH_LOG_PREFIX} profile fetch timeout/failure, continuing with null profile`, {
          userId: nextUser.id,
          reason: error?.message || 'unknown',
        });
      }
      if (cancelled) return;
      setProfile(nextProfile);

      const access = resolveAdminAccess({ user: nextUser, profile: nextProfile });
      setIsAdmin(access.isAdmin);
      setAdminRole(access.role);
      setAdminReason(access.reason);
      devLog.info(`${AUTH_LOG_PREFIX} hydrated signed-in state`, {
        userId: nextUser.id,
        isAdmin: access.isAdmin,
        role: access.role,
      });
    };

    const initFallbackTimer = setTimeout(() => {
      if (isInitialSettled || cancelled) return;
      devLog.warn(`${AUTH_LOG_PREFIX} init timeout fallback fired`);
      isInitialSettled = true;
      safeSetLoading(false);
    }, AUTH_INIT_TIMEOUT_MS + 400);

    const initAuth = async () => {
      try {
        devLog.info(`${AUTH_LOG_PREFIX} getSession start`);
        const sessionResponse = await withTimeout(
          supabase.auth.getSession(),
          AUTH_INIT_TIMEOUT_MS,
          'auth_init_timeout'
        );
        const session = sessionResponse?.data?.session ?? null;
        devLog.info(`${AUTH_LOG_PREFIX} getSession resolved`, {
          hasSession: Boolean(session),
          userId: session?.user?.id ?? null,
        });
        await hydrateAuthState(session?.user ?? null);
      } catch (error) {
        devLog.error(`${AUTH_LOG_PREFIX} init failed, falling back to guest mode`, {
          reason: error?.message || 'unknown',
        });
        await hydrateAuthState(null);
      } finally {
        if (!isInitialSettled) {
          isInitialSettled = true;
          safeSetLoading(false);
        }
        devLog.info(`${AUTH_LOG_PREFIX} init complete`);
      }
    };

    initAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      devLog.info(`${AUTH_LOG_PREFIX} onAuthStateChange`, {
        event: _event,
        hasSession: Boolean(session),
        userId: session?.user?.id ?? null,
      });
      try {
        await hydrateAuthState(session?.user ?? null);
      } catch (error) {
        devLog.error(`${AUTH_LOG_PREFIX} state change handling failed`, {
          reason: error?.message || 'unknown',
        });
      } finally {
        safeSetLoading(false);
      }
    });

    return () => {
      cancelled = true;
      clearTimeout(initFallbackTimer);
      devLog.info(`${AUTH_LOG_PREFIX} cleanup`);
      subscription.unsubscribe();
    };
  }, []);

  const signUp = async (email, password) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    return { data, error };
  };

  const resendConfirmationEmail = async (email) => {
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    return { error };
  };

  const requestPasswordReset = async (email) => {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    return { data, error };
  };

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    return { data, error };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    return { error };
  };

  // Expose helper so other modules can check if a user was ever authenticated on this device
  const hasEverBeenAuthenticated = () => {
    try {
      return localStorage.getItem(WAS_EVER_AUTHENTICATED_KEY) === 'true';
    } catch (_error) {
      return false;
    }
  };

  // Re-fetches the profile from Supabase and updates the shared auth state.
  // Call this after any operation that mutates profile columns (contributor_form,
  // artist_name, etc.) so the rest of the app sees the updated values immediately
  // without waiting for a full re-auth or page reload.
  const refreshProfile = async () => {
    const currentUser = user;
    if (!currentUser?.id) return;
    try {
      const nextProfile = await fetchUserProfile(currentUser.id);
      setProfile(nextProfile);
    } catch (_error) {
      // best effort — stale profile is better than a crash
    }
  };

  const value = {
    user,
    profile,
    isAdmin,
    adminRole,
    adminReason,
    loading,
    signUp,
    signIn,
    signOut,
    resendConfirmationEmail,
    requestPasswordReset,
    hasEverBeenAuthenticated,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
